import { PlusCircleSVG, TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { Button, IconButton } from 'components/elements/buttons';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import { HalfContainer, SeparatorVertical } from 'components/styles';
import { Row, SectionContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { RightButtonContainer, InputContainer } from '../../style';
import { TourListFormType } from '../form-type';
import { onAddNewPrice } from '../hooks/use-get-default-values';

export default function TourPriceSection() {
  const { t } = useTranslation();
  const name = 'tourPrices' as const;

  const { clearErrors, formState, setValue, getValues, trigger } =
    useFormContext<TourListFormType>();

  const { editable } = useFormState();

  const fields = getValues(name);

  const singleItem = fields.length === 1;

  const onAdd = React.useCallback(() => {
    setValue(name, [...fields, onAddNewPrice(fields.length)]);
    clearErrors();
  }, [clearErrors, fields, setValue]);

  const onRemove = React.useCallback(
    (index: number) => {
      const tourPrices = getValues(name);

      tourPrices.splice(index, 1);

      const hasDefault = tourPrices.some((tp) => tp.isDefault);

      setValue(
        name,
        tourPrices.map((tp, index) => {
          return {
            ...tp,
            isDefault: hasDefault ? tp.isDefault : index === 0,
          };
        }),
      );
      trigger(name);
    },
    [getValues, setValue, trigger],
  );

  return (
    <div>
      <Row>
        <Text variant="HeadingXSmall">{t('common:tour_price')}</Text>
        <Separator gap={16} />

        {formState?.errors?.tourPrices && (
          <>
            <Text variant="LabelMedium" color="red">
              {formState?.errors?.tourPrices.message}
            </Text>
          </>
        )}
      </Row>
      <SeparatorVertical $margin={16} />

      {fields.map((field, idx) => (
        <Row
          $style={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
          key={field.customId}
        >
          <SectionContainer>
            <HalfContainer>
              <InputContainer>
                <Input
                  type="text"
                  label={t('common:participant_type')}
                  name={`${name}.${idx}.type`}
                  placeholder={t('common:participant_type')}
                  required
                  noMargin
                />
              </InputContainer>
            </HalfContainer>
            <Separator gap={24} direction="horizontal" />
            <HalfContainer>
              <Input
                type="number"
                label={t('common:price')}
                name={`${name}.${idx}.price`}
                placeholder={t('common:price')}
                required
                noMargin
              />
            </HalfContainer>
            <Separator gap={16} direction="horizontal" />

            <RightButtonContainer>
              <Input
                type="check-box"
                label={t('common:set_as_default')}
                name={`${name}.${idx}.isDefault`}
                onAfterChange={(val) => {
                  fields.forEach((_, index) => {
                    setValue(
                      `${name}.${index}.isDefault`,
                      index === idx ? val : false,
                    );
                  });
                  clearErrors(name);
                }}
                noMargin
                disabled={singleItem}
              />
              {fields.length > 1 && editable && (
                <IconButton
                  buttonType="tertiary"
                  overrides={{
                    Root: {
                      style: {
                        height: '100%',
                        ':hover': {
                          backgroundColor: stylesColors.redLightest,
                        },
                      },
                    },
                  }}
                  onClick={() => onRemove(idx)}
                >
                  <TrashSVG color={stylesColors.redDefault} />
                </IconButton>
              )}
            </RightButtonContainer>
          </SectionContainer>
        </Row>
      ))}

      {editable ? (
        <Button
          startEnhancer={(color, size) => (
            <PlusCircleSVG color={color} size={size} />
          )}
          buttonType="secondary"
          onClick={onAdd}
        >
          {t('common:add')}
        </Button>
      ) : (
        <></>
      )}
    </div>
  );
}
